import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

type EmptyProps = React.ComponentProps<"div"> & {
  /** Announces the message to assistive technology when it appears or changes, for an empty state that replaces content (a status message). */
  live?: boolean
}

/**
 * Empty state of a list, search result or region, centered in its parent. Compose it from `EmptyHeader` (`EmptyMedia`,
 * `EmptyTitle`, `EmptyDescription`) and an `EmptyContent` with the actions. Set `live` when it replaces content that
 * was there a moment ago.
 */
function Empty({ className, live = false, ...props }: EmptyProps) {
  return (
    <div
      data-part="empty"
      role={live ? "status" : undefined}
      className={cn(
        "flex w-full min-w-0 flex-1 flex-col items-center justify-center gap-4 rounded-xl border-dashed p-6 text-center text-balance",
        className
      )}
      {...props}
    />
  )
}

/** Groups the `EmptyMedia`, `EmptyTitle` and `EmptyDescription` of an `Empty` in a narrow centered column. */
function EmptyHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-part="empty-header" className={cn("flex max-w-sm flex-col items-center gap-2", className)} {...props} />
  )
}

const emptyMediaVariants = cva(
  "mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground [&_svg:not([class*='size-'])]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

/** The picture or icon at the top of an `EmptyHeader`. */
function EmptyMedia({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & {
  /** `icon` puts the child, an icon, in a small rounded tile. `default` shows the child without a tile. Defaults to `default`. */
  variant?: VariantProps<typeof emptyMediaVariants>["variant"]
}) {
  return (
    <div
      data-part="empty-icon"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  )
}

/** The short heading of an `Empty`, such as "No results". Put it in an `EmptyHeader`. */
function EmptyTitle({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-part="empty-title" className={cn("text-ui-14 font-semibold tracking-tight", className)} {...props} />
}

/** One or two lines of muted text under the `EmptyTitle`, saying why it is empty or what to do. Links in it are underlined. */
function EmptyDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <div
      data-part="empty-description"
      className={cn(
        "text-ui-14 text-muted-foreground [&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      )}
      {...props}
    />
  )
}

/** The area under the `EmptyHeader` of an `Empty` for what the person can do about it, such as a `Button`, stacked and centered. */
function EmptyContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-part="empty-content"
      className={cn("flex w-full max-w-sm min-w-0 flex-col items-center gap-2.5 text-ui-14 text-balance", className)}
      {...props}
    />
  )
}

export type { EmptyProps }
export { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent, EmptyMedia }
